import { useState } from 'react';
import { Box, Stack, Typography } from '@mui/material';
import { createStyles, WithStyles, withStyles } from '@mui/styles';
import Modal from '~/components/common/Modal';
import themes from '~/themes';
import EmailIcon from '~/assets/images/icons/email.svg';
import LocationIcon from '~/assets/images/icons/pin-location.svg';
import CantectLogo from '~/assets/images/icons/cantec-web-color.svg';
import { getContrastColor } from '~/utils/common';
import { Track } from '~/pages/Track';
import moment from 'moment';
import momentTz from 'moment-timezone';

const exampleDataTracking = () => ({
  status: 'unassigned',
  org_name: 'Profile 3 - Cova 290 (Dispensary Cannabis Store)',
  org_tier: 2,
  message:
    'Your order has been received and is currently being processed for delivery.',
  duration: null,
  order_number: '12345',
  progress_bar: 0.25,
  plate_number: '',
  vehicle_body: ' - ',
  vehicle: {},
  delivery_name: 'John Doe',
  address_1: '1111 Canada Way',
  address_body: 'Burnaby, BC V5G 4X7',
  pickup: {
    name: 'John Doe',
    address_1: '1111 Canada Way',
    city: 'Burnaby',
    state: 'BC',
    country: 'CA',
    postcode: 'V5G 4X7',
  },
  created_at: moment().utc().format('YYYY-MM-DDTHH:mm:ss.SSSSSS'),
  timezone: momentTz.tz.guess(),
});

const style = () =>
  createStyles({
    container: {
      '& .MuiDialogContent-root': {
        backgroundColor: '#fff !important',
        marginTop: '-20px',
        display: 'flex',
      },
    },
    title: {
      'padding': '16px !important',
      '& h6': {
        fontSize: `18px !important`,
        color: themes.color.black,
        fontWeight: 500,
      },
    },
  });

const TABS = [
  {
    id: 'email',
    label: 'Outbound Email',
    icon: EmailIcon,
  },
  {
    id: 'tracking',
    label: 'Tracking Page',
    icon: LocationIcon,
  },
];

type Props = {
  brand_color?: string;
  brand_logo?: any;
  open: boolean;
  onClose: () => void;
};

const Preview = ({
  brand_color = '',
  brand_logo = null,
  open,
  onClose,
  classes,
}: Props & WithStyles<typeof style>) => {
  const [tabSelected, setTabSelected] = useState(TABS[0].id);

  const renderEmail = () => (
    <Stack
      sx={{
        display: 'flex',
        flexDirection: 'column',
        gap: 3.2,
        maxWidth: 550,
        width: '100%',
        margin: 'auto',
      }}
    >
      <Box
        sx={{
          height: 32,
        }}
      >
        {brand_logo ? (
          <img
            src={brand_logo}
            alt='logo'
            style={{
              maxWidth: '100%',
              maxHeight: '100%',
              objectFit: 'contain',
              objectPosition: 'center',
            }}
          />
        ) : (
          <Typography
            fontSize={20}
            fontWeight={600}
            sx={{
              display: 'flex',
              flexxDirection: 'row',
              alignItems: 'center',
              gap: 0.4,
            }}
          >
            <img
              style={{
                width: '24px',
              }}
              src={CantectLogo}
              alt='logo'
            />
            CanFleet
          </Typography>
        )}
      </Box>
      <Box
        sx={{
          backgroundColor: 'white',
          p: 4,
          display: 'flex',
          flexDirection: 'column',
          gap: 2.4,
          borderRadius: '8px',
        }}
      >
        <Typography fontSize={20} fontWeight={600}>
          Order is being processed
        </Typography>
        <Typography fontSize={16}>
          Your order has been received and is currently being processes for
          delivery.
        </Typography>
        <Box
          sx={{
            borderRadius: '6px',
            backgroundColor: brand_color || '#33363D',
            color: getContrastColor(brand_color || '#33363D'),
            p: '6px 8px',
            width: 'fit-content',
            fontWeight: 500,
          }}
        >
          Track Delivery
        </Box>
      </Box>
      <Box
        sx={{
          py: 2.4,
        }}
      >
        <Typography>
          If you didn’t make this request, please ignore this email.
        </Typography>
        <Typography mt={2}>© 2025 CanFleet</Typography>
      </Box>
    </Stack>
  );

  const renderTrackingLink = () => (
    <Stack
      sx={{
        display: 'flex',
        flexDirection: 'column',
        maxWidth: 400,
        width: '100%',
        margin: 'auto',
        backgroundColor: '#fff',
        pb: 3,
        zIndex: 0,
      }}
    >
      <Track
        preview
        exampleData={{
          ...exampleDataTracking(),
          brand_color,
        }}
      />
    </Stack>
  );

  return (
    <Modal
      title='Preview'
      open={open}
      onClose={onClose}
      disableCloseOutside
      PaperProps={{
        sx: {
          width: 800,
          minHeight: 'min(725px, calc(100vh - 40px))',
        },
      }}
      maxWidth='lg'
      actions={[]}
      className={classes.container}
      customTitle={classes.title}
      closeButtonStyles={{
        sx: {
          top: '18px !important',
        },
      }}
      customContentModal='content'
    >
      <Stack
        sx={{
          flex: 1,
          display: 'flex',
          flexDirection: 'column',
          paddingTop: 1.6,
          gap: 1.6,
          color: themes.color.violet500,
        }}
      >
        <Stack
          sx={{
            display: 'flex',
            flexDirection: 'row',
            gap: 0.8,
          }}
        >
          {TABS.map((tab) => (
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'row',
                alignItems: 'center',
                gap: 0.4,
                cursor: 'pointer',
                backgroundColor:
                  tabSelected === tab.id ? '#EDF0F7' : 'transparent',
                borderRadius: '4px',
                padding: '4px 8px',
              }}
              onClick={() => setTabSelected(tab.id)}
            >
              <img src={tab.icon} alt='icon' />
              <Typography
                sx={{
                  fontFamily: 'Lato',
                }}
              >
                {tab.label}
              </Typography>
            </Box>
          ))}
        </Stack>
        <Stack
          sx={{
            p: 3.2,
            backgroundColor: '#F6F6F6',
            borderRadius: '4px',
            flex: 1,
          }}
        >
          {tabSelected === 'email' ? renderEmail() : renderTrackingLink()}
        </Stack>
      </Stack>
    </Modal>
  );
};

export default withStyles(style)(Preview);
